/**
 * Toast — the shared notification system for SiteOperations.io.
 *
 * Mount <ToastProvider> once per module shell; call `toast(message, opts)`
 * from `useToast()` anywhere below it. Toasts stack bottom-right, auto-dismiss
 * (default 4s), and are announced politely to assistive tech. Replaces the
 * per-module toast hosts (and the banned `window.alert`).
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastOptions {
  type?: ToastType;
  /** Auto-dismiss delay in ms (default 4000). Pass 0 to keep until dismissed. */
  duration?: number;
}

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  toast: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Leading status icon per type — color carries the meaning alongside the icon shape. */
const TYPE_ICONS: Record<ToastType, { Icon: typeof CheckCircle; cls: string }> = {
  success: { Icon: CheckCircle, cls: 'text-green-600 dark:text-green-400' },
  error: { Icon: AlertCircle, cls: 'text-red-600 dark:text-red-400' },
  info: { Icon: Info, cls: 'text-primary-600 dark:text-primary-400' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, opts?: ToastOptions) => {
      const id = nextId.current++;
      setToasts((ts) => [...ts, { id, message, type: opts?.type ?? 'success' }]);
      const duration = opts?.duration ?? 4000;
      if (duration > 0) window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div aria-live="polite" className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => {
          const { Icon, cls } = TYPE_ICONS[t.type];
          return (
            <div
              key={t.id}
              className="flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            >
              <Icon className={`h-5 w-5 flex-shrink-0 ${cls}`} />
              <p className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">{t.message}</p>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                className="-m-1 rounded p-1 text-gray-400 transition-colors duration-150 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/** Access the toast function. Must be called below a <ToastProvider>. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>');
  return ctx;
}
